import { ExoticBet, ExoticRow, QuaddieRow, isQuaddieRow } from '../../queries/predictedExotics';

export interface SummaryStats {
  total: number;
  pending: number;
  /** counts[i] = rows with exactly i correct, so counts[target] is the full hit. */
  counts: number[];
  target: number;
}

export function getDefaultDates(): { start: string; end: string } {
  const today = new Date();
  const sixDaysAgo = new Date(today);
  sixDaysAgo.setDate(today.getDate() - 6);
  return {
    start: sixDaysAgo.toISOString().slice(0, 10),
    end: today.toISOString().slice(0, 10),
  };
}

export function parseRaceId(raceId: string): { date: string; identifier: string } {
  // race_id format assumed to start with YYYYMMDD
  if (raceId.length >= 8 && /^\d{8}/.test(raceId)) {
    const year = raceId.slice(0, 4);
    const month = raceId.slice(4, 6);
    const day = raceId.slice(6, 8);
    const rest = raceId.slice(8);
    return { date: `${day}/${month}/${year}`, identifier: rest };
  }
  return { date: '', identifier: raceId };
}

/**
 * Legs won, or -1 while the bet is still alive and undecided. A quaddie dies
 * the moment one leg misses, so a missed leg settles the row even if later
 * legs have not run.
 */
export function quaddieCorrectCount(row: QuaddieRow): number {
  const won = row.legs.filter((leg) => leg.won === true).length;
  const missed = row.legs.some((leg) => leg.won === false);
  const pending = row.legs.some((leg) => leg.won === null);
  if (!missed && pending) return -1;
  return won;
}

/** Combinations a punter would have to cover: the legs' horse counts multiplied out. */
export function quaddieCombinations(row: QuaddieRow): number {
  return row.legs.reduce((total, leg) => total * leg.horses.length, 1);
}

/**
 * Combinations in a BOXED bet on `horseCount` horses — the box covers our
 * selections in any finishing order, which is what these screens report. A
 * first four and a trifecta pay on the order home, so the box has to buy every
 * arrangement of the placings (4 horses boxed = 24); a quinella pays either
 * way round, so a pair is a single combination. Nominating one exact order
 * would be a single combination in every case, and is not what we price here.
 */
export function straightCombinations(bet: ExoticBet, horseCount: number): number {
  if (bet === 'quinella') return (horseCount * (horseCount - 1)) / 2;
  const places = bet === 'first-four' ? 4 : 3;
  if (horseCount < places) return 0;
  let combinations = 1;
  for (let i = 0; i < places; i++) combinations *= horseCount - i;
  return combinations;
}

const AUD = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });

/** What the bet costs for 100% of the dividend: $1 on every combination. */
export function formatOutlay(combinations: number): string {
  return AUD.format(combinations);
}

export function getCorrectCount(row: ExoticRow): number {
  return isQuaddieRow(row) ? quaddieCorrectCount(row) : row.correct_count;
}

export interface MatchBadgeInfo {
  label: string;
  colourClass: string;
}

// Deepest green for a full hit, shading down to red for nothing right.
const BADGE_COLOURS = ['bg-red-500', 'bg-orange-500', 'bg-amber-500', 'bg-blue-500'];

export function getMatchBadgeInfo(correctCount: number, target: number): MatchBadgeInfo {
  if (correctCount === -1) return { label: 'Pending', colourClass: 'bg-gray-400' };
  if (correctCount >= target) return { label: `✓ All ${target} Correct`, colourClass: 'bg-emerald-500' };
  const fromTop = target - correctCount; // 1 = one short, and so on
  return {
    label: `${correctCount} / ${target}`,
    colourClass: BADGE_COLOURS[Math.max(0, BADGE_COLOURS.length - fromTop)] ?? 'bg-red-500',
  };
}

/** Fill state of each correct-count dot; pending (-1) renders all empty. */
export function getCorrectCountDotStates(correctCount: number, target: number): boolean[] {
  return Array.from({ length: target }, (_, i) => i < correctCount);
}

export function computeStats(rows: ExoticRow[], target: number): SummaryStats {
  const stats: SummaryStats = {
    total: 0,
    pending: 0,
    counts: Array.from({ length: target + 1 }, () => 0),
    target,
  };
  for (const row of rows) {
    stats.total++;
    const correct = getCorrectCount(row);
    if (correct === -1) stats.pending++;
    else stats.counts[Math.min(correct, target)]++;
  }
  return stats;
}

/** "N found · N resulted · X.X% hit" for the Results panel description. */
export function formatResultSummary(stats: SummaryStats, noun: string): string {
  const resulted = stats.total - stats.pending;
  const hits = stats.counts[stats.target] ?? 0;
  const hitRate = resulted > 0 ? ((hits / resulted) * 100).toFixed(1) : '0.0';
  return `${stats.total} ${noun}${stats.total !== 1 ? 's' : ''} found · ${resulted} resulted · ${hitRate}% hit all ${stats.target}`;
}
